import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';
import { updateUserPassword } from '../../services/authService';
import { toast } from 'react-toastify';

interface ChangePasswordModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

interface PasswordValidation {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ show, onHide, onSuccess }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validatePassword = (password: string): PasswordValidation => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
  };

  const validation = validatePassword(formData.newPassword);
  const isPasswordValid = Object.values(validation).every(Boolean);
  const passwordsMatch = formData.newPassword === formData.confirmPassword;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError('A nova senha não atende aos critérios de segurança');
      return;
    }

    if (!passwordsMatch) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('A nova senha deve ser diferente da senha atual');
      return;
    }

    setLoading(true);
    const result = await updateUserPassword(formData.currentPassword, formData.newPassword);
    
    if (result.success) {
      toast.success('Senha alterada com sucesso! Você será deslogado para fazer login novamente.');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      onHide();
      // Aguardar um pouco para o usuário ver o toast antes de fazer logout
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } else {
      setError(result.error || 'Erro ao alterar senha');
    }
    
    setLoading(false);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setError('');
      onHide();
    }
  };

  const ValidationItem: React.FC<{ isValid: boolean; text: string }> = ({ isValid, text }) => (
    <div className="d-flex align-items-center gap-2">
      {isValid ? (
        <CheckCircle size={16} className="text-success" />
      ) : (
        <XCircle size={16} className="text-danger" />
      )}
      <span className={isValid ? 'text-success' : 'text-muted'}>{text}</span>
    </div>
  );

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Lock size={24} className="text-primary" />
          Alterar Senha
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Senha Atual</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showPasswords.current ? 'text' : 'password'}
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                required
                placeholder="Digite sua senha atual"
                disabled={loading}
              />
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className="position-absolute end-0 top-50 translate-middle-y me-2 border-0 bg-transparent"
                onClick={() => togglePasswordVisibility('current')}
                disabled={loading}
              >
                {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Nova Senha</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                required
                placeholder="Digite sua nova senha"
                disabled={loading}
                className={formData.newPassword && !isPasswordValid ? 'is-invalid' : ''}
              />
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className="position-absolute end-0 top-50 translate-middle-y me-2 border-0 bg-transparent"
                onClick={() => togglePasswordVisibility('new')}
                disabled={loading}
              >
                {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
            
            {formData.newPassword && (
              <div className="mt-2 p-3 bg-light rounded">
                <small className="text-muted d-block mb-2">Critérios de segurança:</small>
                <div className="d-flex flex-column gap-1">
                  <ValidationItem isValid={validation.length} text="Pelo menos 8 caracteres" />
                  <ValidationItem isValid={validation.uppercase} text="Uma letra maiúscula" />
                  <ValidationItem isValid={validation.lowercase} text="Uma letra minúscula" />
                  <ValidationItem isValid={validation.number} text="Um número" />
                  <ValidationItem isValid={validation.special} text="Um caractere especial" />
                </div>
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Confirmar Nova Senha</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Confirme sua nova senha"
                disabled={loading}
                className={formData.confirmPassword && !passwordsMatch ? 'is-invalid' : ''}
              />
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className="position-absolute end-0 top-50 translate-middle-y me-2 border-0 bg-transparent"
                onClick={() => togglePasswordVisibility('confirm')}
                disabled={loading}
              >
                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
            {formData.confirmPassword && passwordsMatch && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <CheckCircle size={16} className="text-success" />
                <small className="text-success">Senhas coincidem</small>
              </div>
            )}
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !isPasswordValid || !passwordsMatch || !formData.currentPassword}
            >
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ChangePasswordModal;
